import { ChangeDetectorRef, Component, effect, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { Router, RouterOutlet, RoutesRecognized, NavigationEnd } from '@angular/router';
import { SplashScreenComponent } from './layout/splash-screen/splash-screen.component';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ThemeService } from './shared/theme.service';
import { TranslationService } from './shared/translation.service';
import { AuthService } from './auth/auth.service';
import { UserSettingService } from './services/user-setting.service';
import { MediaService } from './services/media.service';
import { SharedService } from './shared/services/shared.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ChatService } from './services/chat/chat.service';
import { catchError, filter, forkJoin, map, Observable, of, pairwise, switchMap, take } from 'rxjs';
import { ToasterService } from './shared/services/toaster.service';

declare var google: any;
@Component({
    selector: 'app-root',
    imports: [
        CommonModule,
        RouterOutlet,
        SplashScreenComponent
    ],
    providers: [AuthService],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = 'Golf.ai';
  isLoading = true;
  isLoggedIn = false;
  // translations: { [key: string]: string } = {};
  constructor(
    private themeService: ThemeService, 
    private translationService: TranslationService, 
    private _authService: AuthService,
    private _settingService: UserSettingService,
    private _sharedService: SharedService,
    private _chatService: ChatService,
    private _router:Router,
    @Inject(PLATFORM_ID) private platformId: object,
    private _mediaService: MediaService,
    private cd: ChangeDetectorRef,
    private _toaster: ToasterService,
    private _translationService: TranslationService,
  ){
    effect(() => {
      this.isLoading = this._sharedService.loading();
    });
    effect(() => {
      this.isLoggedIn = this._sharedService.loggedIn();
      if(this.isLoggedIn && !this._sharedService.userSettingsLoaded()){
        this._sharedService.userSettingsLoaded.set(true);
        this.getUserSetting();
      }
    });
  }
  ngOnInit(): void {
    // subscribe router event
    this._router.events.pipe(filter((evt: any) => evt instanceof RoutesRecognized), pairwise())
    .subscribe((events: RoutesRecognized[]) => {
      this._sharedService.setPreviousUrl(events[0].urlAfterRedirects);
    });
    
    // Add scroll to top functionality on route changes
    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      if (isPlatformBrowser(this.platformId)) {
        setTimeout(() => {
          window.scrollTo(0, 0);
          const leftBlock = document.querySelector('.left_block');
          if (leftBlock) {
            leftBlock.scrollTo(0, 0);
          }
        }, 100);
      }
    });
    if (isPlatformBrowser(this.platformId)) {
      if(this._sharedService.getLocalStorage('golfTap')){
        this._sharedService.golfTap();
      }
      this.themeService.isDarkTheme.subscribe((isDarkTheme) => {
        if (isDarkTheme) {
          document.body.classList.add('dark-theme');
        } else {
          document.body.classList.remove('dark-theme');
        }
      });

      this.isLoggedIn = this._authService.isSignedIn();
      if(this.isLoggedIn && !this._sharedService.userSettingsLoaded()){
        this._sharedService.userSettingsLoaded.set(true);
        this.getUserSetting();
      }
    }
  }
  
  //  Commenting here as we are calling the same in the header component

  getUserSetting() {
    this._settingService.getSetting().pipe(
      switchMap(res => {
        const user = { ...res };
        const mediaRequests: Observable<any>[] = [];
        if (user.caddy?.image) {
          const caddyMedia$ = this._mediaService.getMedia([user.caddy.image]).pipe(
            catchError(error => {
              return of(null);
            }),
            map(mediaRes => ({ type: 'caddy', result: mediaRes }))
          );
          mediaRequests.push(caddyMedia$);
        }
  
        if (user.image) {
          if(user.image.includes("i.ibb.co")){
            user.imageUrl = user.image;
          }
          else{
            const userMedia$ = this._mediaService.getMedia([user.image]).pipe(
              catchError(error => {
                // console.error('Failed to load user image:', error);
                return of(null);
              }),
              map(mediaRes => ({ type: 'user', result: mediaRes }))
            );
            mediaRequests.push(userMedia$);
        }
        }
  
        return mediaRequests.length > 0
          ? forkJoin(mediaRequests).pipe(map(results => ({ user, results })))
          : of({ user, results: [] });
      }),
      take(1)
    ).subscribe({
      next: ({ user, results }) => {
        results.forEach(result => {
          if (!result) return;
          if (result.type === 'caddy' && result.result?.[0]?.url) {
            user.caddy = { ...user.caddy, imageUrl: result.result[0].url };
          }
          if (result.type === 'user' && result.result?.[0]?.url) {
            user.imageUrl = result.result[0].url;
          }
        });
        this._sharedService.setUser(user);
        this._translationService.setLanguage(user.language?.code || 'en');
        this._sharedService.loading.set(false);
        this._sharedService.loggedIn.set(true);
        // this.router.navigateByUrl('/home');
        let socketEnabled = false;
        if (!socketEnabled) {
          // this._chatService.onListenWebsocket();
          socketEnabled = true;
        }
      },
      error: (error) => {
        this._toaster.error('Error fetching user settings:', error);
        this._sharedService.loading.set(false);
        // Consider implementing error handling logic here
      }
    });
  }
  
  
  setLanguage(lang: string): void {
    this.translationService.setLanguage(lang);
  }

  getReverseGeocodingData(lat: any, lng: any): void {
    if (google){
      const latLong = new google.maps.LatLng(lat, lng);
      // This is making the Geocode request
      const geoCoder = new google.maps.Geocoder();
      geoCoder.geocode({ latLng: latLong },  (results: any, status: any) => {
          if (status !== google.maps.GeocoderStatus.OK) {
              alert(status);
          }
          // This is checking to see if the Geoeode Status is OK before proceeding
          // if (status === google.maps.GeocoderStatus.OK) {
          //   if (results.length > 3){
          //     this.sharedService.location = results[3].formatted_address;
          //   } else if (results.length > 2){
          //     this.sharedService.location = results[2].formatted_address;
          //   } else {
          //     this.sharedService.location = results[1].formatted_address;
          //   }
          // }
      });
    }
  }

}
